//=================================================
// On the Hour
//=================================================

import { getReducedMotion, ignoreUserPreferences } from './user-preferences';
import { isbot } from 'isbot';

// Track handlers globally so we can add/remove listeners as needed
let artportMessageHandler;
let onTheHourStart;
let onTheHourEnd;

// Run any necessary project specific code outside the iframe
function projectSpecificStart(hour = new Date().getHours()) {
    // Custom behavior should ideally be passed through postMessage, and run here if present
    if (onTheHourStart !== undefined) {
        onTheHourStart();
    }
}

// Clean up any project specific code that ran outside the iframe
async function projectSpecificEnd() {
    // Custom behavior should ideally be passed through postMessage, and run here if present
    if (onTheHourEnd !== undefined) {
        onTheHourEnd();
    }
}

// Returns a Date object representing the top of the current hour
function getTopOfCurrentHour(date = new Date()) {
    const topOfCurrentHour = new Date(date);
    topOfCurrentHour.setMinutes(0, 0, 0);
    return topOfCurrentHour;
}

// Checks if the supplied time is within the first minute of the current hour
function isWithinFirstMinute(suppliedDate, currentDate = new Date()) {
    return suppliedDate.getHours() === currentDate.getHours() &&
        suppliedDate.getMinutes() === 0 &&
        suppliedDate.getSeconds() < 60;
}

// Calculate time difference in seconds between two dates
function timeDistance(a, b) {
    const newA = a instanceof Date ? a.getTime() : a;
    const newB = b instanceof Date ? b.getTime() : b;
    return Math.abs(Math.floor(newA / 1000) - Math.floor(newB / 1000)); // Return difference in seconds
}

// Checks if the stored timestamp is leftover from a different "top of hour"
// ...the thought being, we don't want to override if someone is quickly changing pages during the critical minute
function isSessionVariableOutOfSync(storedDate, currentDate) {
    const topOfCurrentHour = getTopOfCurrentHour(currentDate);

    return timeDistance(storedDate, topOfCurrentHour) > 60; // Returns true if out of sync
}

// Sets a session storage variable for tracking the on-the-hour event
function setSessionVariable(currentDate) {
    const storedTimestamp = sessionStorage.getItem('on-the-hour');
    const storedDate = storedTimestamp ? new Date(storedTimestamp) : null;

    // Only set a new session variable if the stored one is out of sync or doesn't exist
    if (!storedDate || isSessionVariableOutOfSync(storedDate, currentDate)) {
        sessionStorage.setItem('on-the-hour', currentDate.toISOString());
    }
}

function urlWithHour(urlString) {
    if (!urlString) {
        return;
    }

    const url = new URL(urlString);
    url.searchParams.set('hour', new Date().getHours());
    return url.toString();
}

function updateCountdown(wrapper) {
    const now = new Date();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();
    const timeEl = wrapper.querySelector('.on-the-hour-countdown__time');

    if (currentMinute === 0) {
        // Show "now" for the first minute of the hour
        timeEl.textContent = 'Now';
    } else {
        // Calculate time until next hour
        const minutesLeft = 59 - currentMinute;
        const secondsLeft = 59 - currentSecond;
        // Format the countdown
        const minuteDisplay = String(minutesLeft).padStart(2, '0');
        const secondDisplay = String(secondsLeft).padStart(2, '0');

        timeEl.textContent = `${ minuteDisplay }:${ secondDisplay }`;
    }
}

// Little text string that counts down to the next play, could be embedded on multiple pages/in HTML components
function setupOnTheHourCountdown() {
    document.querySelectorAll('.on-the-hour-countdown').forEach((wrapper) => {
        if (wrapper.dataset.initialized === 'true') {
            return;
        }
        wrapper.dataset.initialized = 'true'; // Only initialize once...

        // Update immediately and then every second
        updateCountdown(wrapper);
        setInterval(() => updateCountdown(wrapper), 1000);
        wrapper.style.display = 'flex';
    });
}

export default function setupOnTheHour() {
    setupOnTheHourCountdown();

    const wrapper = document.querySelector('.on-the-hour');
    if (!wrapper || wrapper.dataset.initialized === 'true') {
        return;
    }
    wrapper.dataset.initialized = 'true'; // Only initialize once...

    const THIRTY_SECONDS = 30000
    // If meta tag is passed OR browser is a bot disable
    const onTheHourParam = new URLSearchParams(window.location.search).get('on-the-hour');

    const shouldDisable = isbot(navigator.userAgent) ||
        document.querySelector('meta[name="on-the-hour"]')?.getAttribute('content') === 'false' ||
        (onTheHourParam === 'false');
    const legend = wrapper ? wrapper.querySelector('.on-the-hour-legend') : null;
    const iframe = wrapper ? wrapper.querySelector('.on-the-hour__iframe') : null;
    const forceOnTheHour = onTheHourParam === 'true';
    const url = wrapper?.dataset.url;
    let currentDate = new Date();

    // If there's no project to load cancel everything
    if (!url) {
        return;
    }

    function onTheHourClose() {
        // Empty the iframe
        if (iframe) {
            iframe.classList.remove('active', 'will-be-active');

            // Because of reduced motion the project may not have loaded, and if it didn't we shouldn't run it's specific cleanup
            if (iframe.src) {
                projectSpecificEnd();
                iframe.src = '';
            }
        }
        wrapper.classList?.remove('reduced-motion');

        scheduleNextCheck();
    }

    // Load in the actual iFrame for the project + any extras
    function loadProject() {
        // Load and display the iframe content
        function loadCallback() {
            iframe.classList.add('active'); // Removes visibility hidden on the project

            projectSpecificStart(); // Run any project specific code (e.g. changes to whitney.org)
            setSessionVariable(currentDate); // Update session storage with the current time
            setTimeout(() => wrapper.close(), THIRTY_SECONDS); // Let the project run for 30 seconds before closing
        }

        iframe.addEventListener('load', loadCallback, { once: true });
        // If this show is in the callback the iframe can be sized incorrectly on initial load
        wrapper.showModal();
        wrapper.focus();
        iframe.classList.add('will-be-active'); // Projects may need iframe size so first display but keep visibility hidden
        iframe.src = urlWithHour(url);

        // Analytics tracking (if enabled)
        if (useAnalytics) {
            dataLayer.push({
                event: 'artport',
                label: 'on_the_hour',
                value: currentDate.getHours(),
            });
        }
    }

    // Triggers the projects if conditions are met and schedules the next one
    function triggerOnTheHour(force = false) {
        // Make sure elements are present and we aren't disabling
        if (shouldDisable || !wrapper || !iframe) {
            return scheduleNextCheck();
        }

        currentDate = new Date();
        const storedTimestamp = sessionStorage.getItem('on-the-hour');
        const storedDate = storedTimestamp ? new Date(storedTimestamp) : null;

        // Only trigger if forced, or if within the first minute of the hour AND:
        // - No stored date exists OR
        // - The stored date exists but is not within the first minute of the current hour
        // ...the goal is to prevent multiple rapid re-fires on refresh or link clicks
        const shouldTrigger = force ||
            (isWithinFirstMinute(currentDate, currentDate) &&
                (!storedDate || !isWithinFirstMinute(storedDate, currentDate)));


        // If no trigger, still schedule the next check
        if (!shouldTrigger) {
            return scheduleNextCheck();
        }

        // Reduced motion users get the option to trigger the project
        if (getReducedMotion() && !ignoreUserPreferences()) {
            // Show the legend
            wrapper.classList.add('reduced-motion');
            wrapper.showModal();
            wrapper.focus();

            // Set so reduced motion modal gets treated as "seeing" the project if they navigate or close it to prevent re-shows
            setSessionVariable(currentDate); // Update session storage with the current time

            // Set a reasonable delay for someone to read and trigger the project (or not)
            setTimeout(() => {
                // If the iframe is active it means the user triggered the project
                // ...if it isn't we should remove the legend since it seems they don't want to
                if (!iframe.src) {
                    wrapper.close();
                    wrapper.classList?.remove('reduced-motion');
                    scheduleNextCheck();
                }
            }, THIRTY_SECONDS);
            return;
        }

        loadProject();
    }

    // Schedules the next check based on the time until the next top of the hour
    function scheduleNextCheck() {
        const now = new Date();

        // Get the top of the current hour
        let nextTopOfHour = getTopOfCurrentHour(now);

        // If the current time is past the top of the current hour, move to the next hour
        if (now.getTime() >= nextTopOfHour.getTime()) {
            nextTopOfHour.setHours(nextTopOfHour.getHours() + 1);
        }

        // Calculate the exact time difference to the next top of the hour
        const timeUntilNextHour = nextTopOfHour.getTime() - now.getTime();

        // Set a timeout to trigger exactly at the next top of the hour
        setTimeout(() => {
            triggerOnTheHour();
        }, timeUntilNextHour);
    }

    // Expose this to the window so we can arbitrarily call it elsewhere (including signage for testing)
    window.forceOnTheHour = () => {
        // Remove existing session variable so it will for sure trigger
        sessionStorage.removeItem('on-the-hour');
        triggerOnTheHour(true)
    }

    // Check for query triggers
    if (forceOnTheHour) {
        window.forceOnTheHour();
    } else {
        // Only trigger if within the first minute of the top of the hour
        if (isWithinFirstMinute(currentDate, currentDate)) {
            triggerOnTheHour();
        } else {
            scheduleNextCheck(); // If not within the first minute, just schedule the next check
        }
    }

    wrapper.addEventListener('close', () => {
        onTheHourClose();
    });

    // Close overlay if click is outside of legend when reduced motion is on
    wrapper.addEventListener('click', (e) => {
        if (wrapper.classList.contains('reduced-motion')) {
            const rect = legend.getBoundingClientRect();
            const insideOverlay = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
                rect.left <= e.clientX && e.clientX <= rect.left + rect.width);

            if (!insideOverlay && !e.target.matches('.on-the-hour-legend__trigger')) {
                wrapper.close();
            }
        }
    });

    // Allow for manual triggering for people with reduced motion on
    wrapper.querySelectorAll('.on-the-hour-legend__trigger').forEach((el) => {
        el.addEventListener('click', () => {
            wrapper.classList.remove('reduced-motion');
            loadProject();

            // Analytics tracking (if enabled)
            if (useAnalytics) {
                dataLayer.push({
                    event: 'artport',
                    label: 'on_the_hour_trigger',
                    value: 'true',
                });
            }
        });
    });

    // Handle clicking any close button
    wrapper.querySelectorAll('.on-the-hour-legend__x').forEach((el) => {
        el.addEventListener('click', () => {
            wrapper.close();
        });
    });

    // Allow artport to post changes to the parent window (depending on the project)
    artportMessageHandler = (e) => {
        if (e.origin === 'https://artport.whitney.org') {
            const data = e.data;
            if (data.code) {
                // Messages might need to be run at specific times (so save for later)
                // If not, then can be run immediately
                if (data.time === 'start') {
                    onTheHourStart = () => eval(data.code);
                } else if (data.time === 'end') {
                    onTheHourEnd = () => eval(data.code);
                } else {
                    eval(data.code);
                }
            }
        }
    };
    window.removeEventListener('message', artportMessageHandler);
    window.addEventListener('message', artportMessageHandler);
}
